import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Download, Printer, ArrowLeft, ArrowRight, ExternalLink, Lock, Headphones } from 'lucide-react';

/**
 * Phase 34 — Content moat.
 *
 * Free preview = 2 protocols (The 3-Breath Reset + The ABC Confidence Stack)
 * fully broken down with steps + timing. These are the most accessible
 * protocols and give the prospect immediate, executable value.
 *
 * The other 4 protocols are NAMED only on this page — full execution
 * detail lives in The Locker Room. This is the McKinsey 7S pattern:
 * publish the framework, lock the playbook.
 *
 * If you're editing this file: do NOT re-expose `steps[]` for protocols
 * #3–#6. The locked tease is the offer for the Locker Room subscription.
 */

const FREE_FRAMEWORKS = [
  {
    n: 1,
    name: 'The 3-Breath Reset',
    when: 'When pressure spikes mid-game or your mind starts spiraling',
    steps: [
      'Breath 1 — Long exhale (6+ seconds). Drop physical tension from shoulders, jaw, and hands.',
      'Breath 2 — Box breath (4 in, 4 hold, 4 out, 4 hold). Anchor your nervous system to a steady rhythm.',
      'Breath 3 — Sharp power inhale + whisper your cue word ("go work," "my time," "reset"). Launch back into the moment.',
    ],
    timing: '30 seconds, no equipment, anywhere.',
    pdf: '/coach-lewis-01-three-breath-reset.pdf',
  },
  {
    n: 2,
    name: 'The ABC Confidence Stack',
    when: 'Pre-performance — to walk in feeling ready, not nervous',
    steps: [
      'A — Anchor: A physical action you OWN (chest tap, wrist squeeze, foot stomp). Same one every time.',
      'B — Belief: A 5-word identity statement. "I belong on this floor." Pick one, marry it.',
      'C — Cue: A sharp verbal trigger ("go work," "my time"). Says: training is over, now we play.',
    ],
    timing: '90 seconds, run it 5 minutes before tip / kickoff / serve.',
    pdf: '/coach-lewis-02-abc-confidence-stack.pdf',
  },
];

// Locked protocols — public can see the name + when-to-use + outcome line.
// Execution detail is Locker Room only.
const LOCKED_FRAMEWORKS = [
  {
    n: 3,
    name: 'Green-Yellow-Red Awareness',
    when: 'In-game zone check — to know which protocol to run.',
    outcome: 'The in-game self-scan that tells you which protocol to run \u2014 before the wheels come off.',
  },
  {
    n: 4,
    name: 'The Camera Drill',
    when: 'Visualization \u2014 building reps before they happen.',
    outcome: 'How elite athletes build muscle memory for moments they haven\u2019t lived yet.',
  },
  {
    n: 5,
    name: 'Bounce-Back 3R',
    when: 'Right after a mistake \u2014 turnover, missed shot, blown coverage.',
    outcome: 'How elite athletes erase the last mistake in 30 seconds \u2014 and why most never learn it.',
  },
  {
    n: 6,
    name: 'The Performance Pyramid',
    when: 'Pre-game when you feel scattered or rushed.',
    outcome: 'The pre-game sequence that turns scattered into sharp in under 15 minutes.',
  },
];

export default function FrameworksDownload() {
  const print = () => window.print();
  return (
    <div className="min-h-screen bg-white text-[#0a0d0e]" data-testid="frameworks-download">
      {/* Non-printing toolbar */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-foreground/10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-sm font-semibold flex items-center gap-1.5 hover:text-[#004C54]" data-testid="back-home">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
          <div className="flex gap-2">
            <Button onClick={print} variant="outline" size="sm" data-testid="frameworks-print" className="hidden sm:inline-flex">
              <Printer className="h-4 w-4 mr-1.5" /> Print
            </Button>
            <Link to="/locker-room">
              <Button size="sm" className="bg-[#004C54] hover:bg-[#003a40] text-white" data-testid="frameworks-cta-locker-top">
                Unlock All 6 <ExternalLink className="h-3 w-3 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-10 sm:py-14">
        <header className="mb-10 pb-6 border-b-2 border-[#004C54]">
          <div className="font-mono-accent uppercase tracking-[0.18em] text-[11px] font-bold text-[#004C54]">A Coach Lewis Playbook &middot; Free Preview</div>
          <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight mt-2">
            Two protocols. <span className="text-[#004C54]">Start with these tonight.</span>
          </h1>
          <p className="text-foreground/75 mt-3 text-sm sm:text-base">
            These are the two most-accessible protocols from <strong>The Apex Six</strong> &mdash; the system Coach Lewis runs with serious middle school, high school, and college athletes. Full execution, free.
          </p>
          <p className="text-foreground/55 mt-2 text-xs sm:text-sm italic">
            The other four protocols (Green-Yellow-Red Awareness, The Camera Drill, Bounce-Back 3R, The Performance Pyramid) live in <Link to="/locker-room" className="underline hover:text-[#004C54]">The Locker Room</Link> &mdash; with audio cues, scripts, and Coach Lewis voiceovers.
          </p>
        </header>

        {/* Free protocols — fully detailed */}
        <div className="space-y-10">
          {FREE_FRAMEWORKS.map((f) => (
            <section key={f.n} data-testid={`fw-${f.n}`}>
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <div className="flex items-baseline gap-3">
                  <div className="font-mono-accent text-[#004C54] font-bold text-lg">#{f.n}</div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{f.name}</h2>
                </div>
                <a
                  href={f.pdf}
                  download
                  className="print:hidden inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#004C54] hover:underline whitespace-nowrap"
                  data-testid={`fw-${f.n}-pdf`}
                >
                  <Download className="h-3.5 w-3.5" /> PDF
                </a>
              </div>
              <p className="text-sm font-semibold text-[#004C54] uppercase tracking-wider mb-3">When to use</p>
              <p className="text-foreground/80 mb-4">{f.when}</p>
              <p className="text-sm font-semibold text-[#004C54] uppercase tracking-wider mb-2">How to run it</p>
              <ol className="space-y-2 list-decimal pl-5 text-foreground/85">
                {f.steps.map((s, i) => <li key={i} className="leading-relaxed">{s}</li>)}
              </ol>
              <p className="text-xs text-foreground/60 mt-3 italic">{f.timing}</p>
            </section>
          ))}
        </div>

        {/* Locked protocols — name + outcome only, no execution detail */}
        <section className="mt-14 pt-10 border-t-2 border-dashed border-[#004C54]/30" data-testid="frameworks-locked-block">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#004C54]/10 text-[#004C54] text-[11px] font-bold uppercase tracking-[0.18em]">
              <Lock className="h-3 w-3" /> Locker Room only
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-3">
              The other 4 protocols.
            </h2>
            <p className="text-foreground/65 mt-2 text-sm sm:text-base max-w-[52ch] mx-auto">
              These are the protocols Coach Lewis runs <em>after</em> the breath and confidence work is installed. Full scripts, timing cues, and audio walk-throughs live in The Locker Room.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {LOCKED_FRAMEWORKS.map((f) => (
              <div
                key={f.n}
                className="relative rounded-2xl border-2 border-foreground/10 bg-[#F5F3EE] p-5"
                data-testid={`fw-locked-${f.n}`}
              >
                <div className="absolute top-3 right-3">
                  <Lock className="h-3.5 w-3.5 text-[#004C54]/40" />
                </div>
                <div className="font-mono-accent text-[#004C54] font-bold text-[12px] mb-1">#{f.n}</div>
                <h3 className="font-heading text-[1.1rem] font-bold tracking-tight mb-1.5">{f.name}</h3>
                <p className="text-[11px] font-semibold text-[#004C54] uppercase tracking-wider mb-2">When to use</p>
                <p className="text-[13px] text-foreground/75 mb-3 leading-snug">{f.when}</p>
                <p className="text-[13px] text-foreground/85 leading-snug italic">&ldquo;{f.outcome}&rdquo;</p>
              </div>
            ))}
          </div>

          <div className="text-center print:hidden">
            <Link to="/locker-room" data-testid="frameworks-cta-locker-mid">
              <Button size="lg" className="bg-[#004C54] hover:bg-[#003a40] text-white font-bold">
                <Headphones className="h-4 w-4 mr-2" /> Unlock all 6 in The Locker Room
              </Button>
            </Link>
            <p className="text-[12px] text-foreground/55 mt-2">Audio drops &middot; Apex Six notecards &middot; Pre-game playlists</p>
          </div>
        </section>

        <footer className="mt-14 pt-8 border-t-2 border-[#004C54] space-y-6">
          {/*
            Paid + free secondary CTAs — Phase 21.4, reframed for Phase 34.
            Now positions the IDP as the upgrade from "2 protocols" to "your
            scored profile across all six" — clearer ladder.
          */}
          <div className="grid sm:grid-cols-2 gap-4 print:hidden" data-testid="frameworks-secondary-ctas">
            <a
              href="/assessment"
              data-testid="frameworks-cta-idp"
              onClick={() => {
                try {
                  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                    window.gtag('event', 'frameworks_cta_click', { label: 'IDP' });
                  }
                } catch {
                  /* analytics best-effort */
                }
              }}
              className="block rounded-2xl bg-gradient-to-br from-[#004C54] to-[#003a40] text-white p-6 ring-1 ring-[#4FBCC7]/30 hover:ring-[#4FBCC7]/60 transition-all"
            >
              <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] font-bold text-[#4FBCC7] mb-1.5">Next level &middot; $27</div>
              <div className="font-heading text-[1.2rem] font-bold mb-1.5">Get your scored Apex Six IDP</div>
              <div className="text-[13px] text-white/75 leading-snug mb-3">
                Your personal score across all six dimensions, your Primary Action, and the printable IDP. The diagnostic that tells you which protocol to install first.
              </div>
              <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#4FBCC7]">
                Start my IDP <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </a>
            <a
              href="/locker-room"
              data-testid="frameworks-cta-locker"
              onClick={() => {
                try {
                  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                    window.gtag('event', 'frameworks_cta_click', { label: 'Locker Room' });
                  }
                } catch {
                  /* analytics best-effort */
                }
              }}
              className="block rounded-2xl bg-[#F5F3EE] text-[#003a40] p-6 ring-1 ring-[#004C54]/15 hover:ring-[#004C54]/35 transition-all"
            >
              <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] font-bold text-[#004C54] mb-1.5">Subscription</div>
              <div className="font-heading text-[1.2rem] font-bold mb-1.5">Unlock all 6 protocols</div>
              <div className="text-[13px] text-[#003a40]/75 leading-snug mb-3">
                The full Apex Six library &mdash; audio drops, scripts, notecards, and pre-game playlists. The execution layer behind the preview you just read.
              </div>
              <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#004C54]">
                Open The Locker Room <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </a>
          </div>
          <div className="hidden print:block text-sm font-semibold text-center mt-4">apexathleticmind.com</div>
        </footer>
      </article>
    </div>
  );
}
